import { useParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import theme from "theme";
import { colors } from "theme/palette";
import { reverse } from "utils/urls";

import Button from "components/Button";
import Divider from "components/Divider";
import Link from "components/Link";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import StickyMenu from "components/StickyMenu";
import Text from "components/Text";

import FieldUpdateIcon from "entities/Field/components/FieldUpdateIcon";
import { useCompanyPackageWithTabsInformation } from "entities/Package/sdk";

export interface ILeftMenu {
  isAllFilesSelected?: boolean;
}

const LeftMenu = ({ isAllFilesSelected }: ILeftMenu) => {
  const params = useParams<{
    dealId: string;
    packageId: string;
    tabId?: string;
  }>();
  const dealId = Number(params.dealId);
  const packageId = Number(params.packageId);
  const tabId: number | undefined = params.tabId
    ? Number(params.tabId)
    : undefined;
  const { data: checklist } = useCompanyPackageWithTabsInformation({
    dealId,
    packageId,
  });

  return (
    <StickyMenu
      top={theme.spacing(13.75)}
      sx={{
        width: "210px",
        maxHeight: "500px",
        borderRadius: "8px",
        border: `1px solid ${colors.blue40}`,
        padding: theme.spacing(2, 2, 0, 2),
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      <Text
        variant="labelSmall"
        fontWeight={500}
        color={colors.gray40}
        data-testid="dd-package-id"
      >
        ID# {packageId}
      </Text>

      <Text
        variant="text2"
        style={{ fontWeight: "bold" }}
        data-testid="dd-package-name"
      >
        {checklist?.name || <Skeleton width={75} />}
      </Text>

      <Text
        variant="text4"
        sx={{ marginBottom: theme.spacing(1), color: colors.gray60 }}
        data-testid="dd-package-investor-company-name"
      >
        {checklist?.company.name || <Skeleton width={100} />}
      </Text>

      {!_.isEmpty(checklist?.tabs) && (
        <>
          <Divider
            sx={{
              borderColor: colors.blue20,
              marginTop: theme.spacing(1),
              marginBottom: theme.spacing(1),
            }}
          />
          <Text
            variant="labelSmall"
            color={colors.gray40}
            fontWeight={500}
            marginBottom={1}
          >
            {checklist?.tabs.length} Tab
            {checklist?.tabs.length === 1 ? "" : "s"}
          </Text>

          <Stack sx={{ overflowY: "auto", paddingBottom: 2 }}>
            {checklist?.tabs.map((tab) => {
              const isSelected = tabId === tab.id;

              return (
                <Link
                  replace
                  to={reverse(URLS.DEAL_PACKAGE, {
                    dealId,
                    packageId,
                    tabId: tab.id,
                  })}
                  linkDataTestid="dd-tab-link"
                >
                  <Button
                    variant={isSelected ? "contained" : "text"}
                    color="secondary"
                    sx={{
                      width: "100%",
                      textAlign: "left",
                      paddingLeft: 1.5,
                      paddingRight: 1,
                      borderLeft: isSelected
                        ? `6px solid ${colors.blue100}`
                        : "0",
                      minHeight: "38px",
                      height: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                    data-testid="dd-tab-name-button"
                  >
                    {tab.name}

                    {tab.updated_field_count > 0 && (
                      <FieldUpdateIcon
                        tooltip={`Tab has ${tab.updated_field_count} field${tab.updated_field_count > 1 ? "s" : ""} updated`}
                        numberOfUpdates={tab.updated_field_count}
                      />
                    )}
                  </Button>
                </Link>
              );
            })}
          </Stack>
        </>
      )}
    </StickyMenu>
  );
};

export default LeftMenu;
